import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { withSpacing, clsx } from '@wds/utils';
import { createUseStyles, useTheme } from 'react-jss';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

var SEMANTIC_COLORS = Object.freeze(['primary', 'success', 'info', 'warning', 'upgrade', 'secondary', 'muted']);
var TYPE_COLORS = Object.freeze(['dark', 'light', 'darkMuted', 'lightMuted', 'link']);
var SIZES = Object.freeze(['lg', 'md', 'sm']);
Object.freeze(['primary', 'upgrade', 'info', 'success', 'warning', 'secondary']);
Object.freeze([].concat(_toConsumableArray(SIZES), ['xs']));
Object.freeze(['md', 'sm']);
Object.freeze(['success', 'warning']);
Object.freeze(['success', 'warning']);
Object.freeze(['data-one', 'data-two', 'data-three', 'data-four', 'data-five', 'data-six', 'data-seven', 'data-eight', 'data-nine', 'data-ten']);
Object.freeze({
  DOWN: 'ArrowDown',
  END: 'End',
  ESC: 'Escape',
  HOME: 'Home',
  LEFT: 'ArrowLeft',
  PAGE_DOWN: 'PageDown',
  PAGE_UP: 'PageUp',
  RIGHT: 'ArrowRight',
  UP: 'ArrowUp',
  ENTER: 'Enter',
  SPACE: ' ',
  BACKSPACE: 'Backspace',
  TAB: 'Tab'
}); // Deprecated. Moving to e.key

Object.freeze({
  DOWN: 40,
  END: 35,
  ESC: 27,
  HOME: 36,
  LEFT: 37,
  PAGE_DOWN: 34,
  PAGE_UP: 33,
  RIGHT: 39,
  UP: 38,
  ENTER: 13,
  SPACE: 32,
  BACKSPACE: 8,
  TAB: 9
});
Object.freeze({
  37: -1,
  38: -1,
  39: 1,
  40: 1
});
Object.freeze(['top', 'bottom', 'right', 'left']);
Object.freeze(['primary', 'secondary', 'muted', 'success', 'warning', 'upgrade', 'info']);
Object.freeze(['light', 'dark']);
Object.freeze(['primary', 'light', 'dark', 'transparent']);
Object.freeze(['left', 'right', 'even', 'center']);
Object.freeze(['normal', 'near', 'far']);
Object.freeze(['up', 'right', 'down', 'left']);
Object.freeze(['slow', 'normal', 'fast']);
Object.freeze({
  speed: {
    slow: 300,
    normal: 200,
    fast: 100
  }
});
Object.freeze({
  speed: {
    slow: 450,
    normal: 350,
    fast: 250
  },
  distance: {
    normal: 25,
    near: 5,
    far: 75
  }
});

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var useStyles = createUseStyles(function (theme) {
  return {
    wrenchTypography: function wrenchTypography(_ref) {
      var align = _ref.align,
          color = _ref.color,
          variant = _ref.variant;
      var typeColor = theme.typeColor,
          semanticColor = theme.semanticColor,
          fontFamily = theme.fontFamily,
          fontSize = theme.fontSize,
          fontWeight = theme.fontWeight;
      var chosenColor = typeColor[color] || semanticColor[color];
      return _objectSpread(_objectSpread({
        fontFamily: fontFamily.base,
        fontSize: fontSize.body,
        boxSizing: 'border-box',
        '-webkit-font-smoothing': 'antialiased',
        color: chosenColor,
        textAlign: align,
        lineHeight: 1.5,
        margin: 0,
        padding: 0
      }, variant && variant !== 'link' && {
        fontSize: fontSize[variant],
        fontWeight: {
          hero1: fontWeight.light,
          hero2: fontWeight.light,
          title: fontWeight.light,
          sectionTitle: fontWeight.light,
          pageTitle: fontWeight.light,
          cardTitle: fontWeight.medium,
          body: fontWeight.regular,
          bodySm: fontWeight.regular
        }[variant]
      }), variant && variant === 'link' && {
        color: typeColor.link,
        textDecoration: 'none',
        border: 'none',
        background: 'none',
        outline: 'none',
        cursor: 'pointer',
        fontWeight: 'inherit',
        fontSize: 'inherit',
        '&:hover, &:focus': {
          textDecoration: 'underline'
        }
      });
    }
  };
});

var _excluded = ["align", "className", "children", "color", "component", "variant"];
var ALIGNMENT = ['center', 'left', 'right'];
var VARIANTS = ['body', 'bodySm', 'cardTitle', 'hero1', 'hero2', 'link', 'pageTitle', 'sectionTitle']; // @todo: custom components wanted like <Link />??

var COMPONENT_LIST = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'p', 'span', 'div', 'article', 'section'];
var Typography = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var align = _ref.align,
      className = _ref.className,
      children = _ref.children,
      color = _ref.color,
      component = _ref.component,
      variant = _ref.variant,
      rest = _objectWithoutProperties(_ref, _excluded);

  var Component = component;
  var theme = useTheme();
  var classes = useStyles({
    align: align,
    color: color,
    variant: variant,
    theme: theme
  });
  var classNames = clsx(className, classes.wrenchTypography);
  return /*#__PURE__*/React.createElement(Component, Object.assign({
    className: classNames
  }, rest, {
    ref: ref
  }), children);
});
Typography.propTypes = {
  /**
   * Align content
   */
  align: PropTypes.oneOf(ALIGNMENT),

  /**
   * Text content to be displayed
   */
  children: PropTypes.node.isRequired,
  className: PropTypes.string,

  /**
   * Color variants
   */
  color: PropTypes.oneOf(SEMANTIC_COLORS.concat(TYPE_COLORS)),

  /**
   * Must be one of the following components:
   */
  component: PropTypes.oneOf(COMPONENT_LIST),

  /**
   * Our variants of choice are:
   */
  variant: PropTypes.oneOf(VARIANTS)
};
Typography.defaultProps = {
  align: 'left',
  className: undefined,
  component: 'div',
  color: 'dark',
  variant: 'body'
};
var TypographyWithSpacing = withSpacing(Typography);
TypographyWithSpacing.displayName = 'Typography';
Typography.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "Typography",
  "props": {
    "align": {
      "defaultValue": {
        "value": "'left'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'center'",
          "computed": false
        }, {
          "value": "'left'",
          "computed": false
        }, {
          "value": "'right'",
          "computed": false
        }]
      },
      "required": false,
      "description": "Align content"
    },
    "className": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "component": {
      "defaultValue": {
        "value": "'div'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'h1'",
          "computed": false
        }, {
          "value": "'h2'",
          "computed": false
        }, {
          "value": "'h3'",
          "computed": false
        }, {
          "value": "'h4'",
          "computed": false
        }, {
          "value": "'h5'",
          "computed": false
        }, {
          "value": "'h6'",
          "computed": false
        }, {
          "value": "'a'",
          "computed": false
        }, {
          "value": "'p'",
          "computed": false
        }, {
          "value": "'span'",
          "computed": false
        }, {
          "value": "'div'",
          "computed": false
        }, {
          "value": "'article'",
          "computed": false
        }, {
          "value": "'section'",
          "computed": false
        }]
      },
      "required": false,
      "description": "Must be one of the following components:"
    },
    "color": {
      "defaultValue": {
        "value": "'dark'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "SEMANTIC_COLORS.concat(TYPE_COLORS)"
      },
      "required": false,
      "description": "Color variants"
    },
    "variant": {
      "defaultValue": {
        "value": "'body'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'body'",
          "computed": false
        }, {
          "value": "'bodySm'",
          "computed": false
        }, {
          "value": "'cardTitle'",
          "computed": false
        }, {
          "value": "'hero1'",
          "computed": false
        }, {
          "value": "'hero2'",
          "computed": false
        }, {
          "value": "'link'",
          "computed": false
        }, {
          "value": "'pageTitle'",
          "computed": false
        }, {
          "value": "'sectionTitle'",
          "computed": false
        }]
      },
      "required": false,
      "description": "Our variants of choice are:"
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "Text content to be displayed"
    }
  }
};

export { TypographyWithSpacing as Typography };
//# sourceMappingURL=index.es.js.map
